import { Link, useLocation } from "react-router-dom";
import { BookOpen, MessageCircleQuestion } from "lucide-react";
import { OfferButtons, QuestionGrid } from "./SearchBits";

export function PassageResult({ data }) {
  const { pathname, search } = useLocation();
  const state = { from: `${pathname}${search}` };
  const ref = data.reference;
  const exact = data.groups.passage || [];
  const chapterQs = data.chapter_questions || [];
  if (ref.invalid) {
    return (
      <section className="rounded-2xl border border-dashed border-border p-8 text-center" data-testid="search-passage-invalid">
        <p className="font-serif text-2xl text-foreground">That reference doesn't exist.</p>
        <p className="mt-2 text-sm text-muted-foreground">{ref.invalid}</p>
        <Link to={`/read/${ref.book}/1`} state={state} className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" data-testid="search-offer-read"><BookOpen className="h-4 w-4" /> Open {ref.book} 1</Link>
      </section>
    );
  }
  if (ref.chapter === null) {
    return (
      <section className="rounded-2xl border border-border bg-card p-8" data-testid="search-passage-book">
        <p className="font-mono text-[11px] uppercase tracking-wider text-gold">Book</p>
        <h2 className="mt-1 font-serif text-3xl text-foreground">{ref.book}</h2>
        <p className="mt-2 text-sm text-muted-foreground">Add a chapter or verse to look up a passage, or browse every recorded question in this book.</p>
        <OfferButtons offers={data.offers} />
      </section>
    );
  }
  return (
    <div className="space-y-6">
      {data.verses?.length > 0 && (
        <section className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="search-passage-verse">
          <p className="font-mono text-[11px] uppercase tracking-wider text-gold">{ref.label} · KJV</p>
          <blockquote className="mt-3 font-serif text-xl leading-relaxed text-foreground sm:text-2xl">
            {data.verses.map((v) => <span key={v.verse}><sup className="mr-1 font-mono text-[11px] text-muted-foreground">{v.verse}</sup>{v.text} </span>)}
          </blockquote>
        </section>
      )}
      {exact.length > 0 ? (
        <section data-testid="search-passage-questions">
          <h2 className="mb-3 flex items-center gap-2 font-serif text-xl font-semibold text-foreground"><MessageCircleQuestion className="h-4 w-4 text-gold" /> {exact.length === 1 ? "The question recorded at" : `${exact.length} questions recorded at`} {ref.label}</h2>
          <QuestionGrid items={exact} />
          {data.offers.length > 0 && <OfferButtons offers={data.offers} />}
        </section>
      ) : (
        <section className="rounded-2xl border border-dashed border-border p-6 sm:p-8" data-testid="search-passage-none">
          <p className="font-serif text-2xl text-foreground">{ref.verse ? `${ref.label} contains no question.` : `No questions are recorded in ${ref.label}.`}</p>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {ref.verse
              ? "QuestionGod indexes the questions people ask in Scripture, and this verse is a statement rather than a question. Here is where to go next:"
              : "This chapter has no recorded questions in our corpus. You can still read it, or explore nearby themes."}
          </p>
          <OfferButtons offers={data.offers} />
        </section>
      )}
      {ref.verse !== null && chapterQs.length > 0 && (
        <section data-testid="search-chapter-questions">
          <h2 className="mb-3 font-serif text-xl font-semibold text-foreground">Questions in {ref.book} {ref.chapter}</h2>
          <QuestionGrid items={chapterQs.filter((q) => !exact.some((e) => e.id === q.id))} />
        </section>
      )}
    </div>
  );
}
